import { createContext, useContext, useEffect, useState } from 'react';
import { apiService } from '../lib/api';

type Theme = 'light' | 'dark';
type ThemeMode = 'light' | 'dark' | 'auto';

interface ThemeContextType {
  theme: Theme;
  themeMode: ThemeMode;
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeMode) => void;
  isDark: boolean;
  isLoading: boolean;
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export function useThemeState() {
  // Get system preference
  const getSystemTheme = (): Theme => {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  };

  // Initialize theme mode from localStorage
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem('chime_theme_mode') as ThemeMode;
    return (stored === 'light' || stored === 'dark' || stored === 'auto') ? stored : 'auto';
  });

  // Calculate actual theme based on mode
  const getActualTheme = (): Theme => {
    if (themeMode === 'auto') {
      return getSystemTheme();
    }
    return themeMode;
  };

  const [theme, setTheme] = useState<Theme>(getActualTheme);
  const [isLoading, setIsLoading] = useState(true);

  // Listen to system theme changes when in auto mode
  useEffect(() => {
    if (themeMode !== 'auto') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      setTheme(getSystemTheme());
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode]);

  // Update theme when mode changes
  useEffect(() => {
    setTheme(getActualTheme());
  }, [themeMode]);

  // Load theme from localStorage first, then sync with backend
  useEffect(() => {
    const loadTheme = async () => {
      // Always load from localStorage first for instant theme application
      const stored = localStorage.getItem('chime_theme_mode') as ThemeMode;
      if (stored && (stored === 'light' || stored === 'dark' || stored === 'auto')) {
        setThemeModeState(stored);
      }

      setIsLoading(false);
    };

    loadTheme();
  }, []);

  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode);
    localStorage.setItem('chime_theme_mode', mode);
  };

  const toggleTheme = () => {
    // Cycle through: auto -> light -> dark -> auto
    let newMode: ThemeMode;
    if (themeMode === 'auto') {
      newMode = 'light';
    } else if (themeMode === 'light') {
      newMode = 'dark';
    } else {
      newMode = 'auto';
    }
    setThemeMode(newMode);
  };

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      root.setAttribute('data-theme', 'dark');
    } else {
      root.classList.remove('dark');
      root.setAttribute('data-theme', 'light');
    }
  }, [theme]);

  return {
    theme,
    themeMode,
    toggleTheme,
    setThemeMode,
    isDark: theme === 'dark',
    isLoading
  };
}
